export type Brand = {
  name: string;
  // Final do nome exibido na cor de destaque (ex.: "Help" em "AmbuHelp"); null = nome inteiro na cor do texto.
  nameHighlight: string | null;
  color: string;
  logoUrl: string;
  faviconUrl: string;
  title: string;
  description: string;
  fontFamily: string;
  fontUrl: string;
  desktopAppEnabled: boolean;
};

type BrandEnv = Record<string, string | undefined>;

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

// Falha no carregamento em vez de subir com a marca errada.
function requireVar(env: BrandEnv, name: string): string {
  const value = env[name]?.trim();
  if (!value) {
    throw new Error(
      `[Brand] ${name} não definida. Confira o arquivo .env.<modo> do build (valores com # ou vírgulas precisam de aspas).`,
    );
  }
  return value;
}

// O Vite injeta o valor cru no index.html (atributos e <title>): aspas, < e > quebrariam a marcação.
function requireHtmlSafeVar(env: BrandEnv, name: string): string {
  const value = requireVar(env, name);
  if (/["<>]/.test(value)) {
    throw new Error(`[Brand] ${name} inválida: não pode conter aspas duplas, < ou >.`);
  }
  return value;
}

function requireHttpsUrl(env: BrandEnv, name: string): string {
  const value = requireHtmlSafeVar(env, name);
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`[Brand] ${name} inválida: "${value}" não é uma URL.`);
  }
  if (url.protocol !== 'https:') {
    throw new Error(`[Brand] ${name} inválida: use https ("${value}").`);
  }
  return value;
}

// Opcional. Como precisa ser o final do nome (já validado contra aspas, < e >), o destaque nasce seguro.
function parseNameHighlight(env: BrandEnv, name: string): string | null {
  const value = env.VITE_BRAND_NAME_HIGHLIGHT?.trim();
  if (!value) return null;
  if (!name.endsWith(value)) {
    throw new Error(`[Brand] VITE_BRAND_NAME_HIGHLIGHT inválida: "${value}" precisa ser o final de VITE_BRAND_NAME ("${name}").`);
  }
  return value;
}

export function parseBrand(env: BrandEnv): Brand {
  const color = requireVar(env, 'VITE_BRAND_COLOR');
  if (!HEX_COLOR.test(color)) {
    throw new Error(`[Brand] VITE_BRAND_COLOR inválida: "${color}". Use #rrggbb.`);
  }
  const name = requireHtmlSafeVar(env, 'VITE_BRAND_NAME');

  return {
    name,
    nameHighlight: parseNameHighlight(env, name),
    color,
    logoUrl: requireHttpsUrl(env, 'VITE_BRAND_LOGO_URL'),
    faviconUrl: requireHttpsUrl(env, 'VITE_BRAND_FAVICON_URL'),
    title: requireHtmlSafeVar(env, 'VITE_BRAND_TITLE'),
    description: requireHtmlSafeVar(env, 'VITE_BRAND_DESCRIPTION'),
    fontFamily: requireVar(env, 'VITE_BRAND_FONT_FAMILY'),
    fontUrl: requireHttpsUrl(env, 'VITE_BRAND_FONT_URL'),
    desktopAppEnabled: env.VITE_BRAND_DESKTOP_APP_ENABLED === 'true',
  };
}
